import { UseQueryResult } from '@tanstack/react-query';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import FlipMove from 'react-flip-move';
import { ContactFilter, ContactFilterCriteriaProperty } from '../../../extViewmodels';
import { useLeadFilterByCompanyQuery } from '../../../queries/Lead/useLeadFilterByCompanyQuery';
import { useLeadFilterByContactQuery } from '../../../queries/Lead/useLeadFilterByContactQuery';
import { useLeadSearchQuery } from '../../../queries/Lead/useLeadSearchQuery';
import { Button } from '../../../web/components-v2/Button';
import { Dialog, DialogBackdrop, DialogPopup } from '../../../web/components-v2/Dialog';
import { Input, InputWrapper } from '../../../web/components-v2/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../web/components-v2/Select';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { SearchIcon2 } from '../../../web/components/svgs/icons/SearchIcon2';
import { bs } from '../../../web/styles/styles';
import PlaceholderImageSource from '../../assets/call-from-unknown-placeholder.svg';
import { ISearchPayload, SearchKeyOptions } from '../../contexts/search';
import { aidaBaseStyleSheet } from '../../styles/styles';
import { CompanyFilterCriteriaProperty, ICompanyFilter } from '../../viewModels/companyfilter';
import { ILeadSearchView } from '../../viewModels/leads/interfaces';
import { LeadServedSource } from '../../viewModels/queue';
import { LeadCard } from '../LeadCard';
import { styleSheet } from './styles';

interface IProps {
	initialQuery?: string;
	isOpen?: boolean;
	onRequestClose?: (result: any, canceled: boolean) => void;
	payload: ISearchPayload;
}

const searchTypeOptions = [
	{ value: 'all', label: 'All' },
	{ value: 'contact', label: 'Contact' },
	{ value: 'company', label: 'Company' },
	{ value: 'phone', label: 'Phone' },
	{ value: 'email', label: 'Email' },
];

export const SearchModalBase: React.FC<IProps> = ({ initialQuery = '', isOpen, onRequestClose, payload }) => {
	const searchInputRef = useRef<HTMLInputElement>(null);
	const [searchValue, setSearchValue] = useState(initialQuery);
	const [searchType, setSearchType] = useState<'all' | 'contact' | 'company' | 'phone' | 'email'>('all');
	let contactFilter: ContactFilter | null = null;
	let companyFilter: ICompanyFilter | null = null;
	if (searchValue) {
		switch (searchType) {
			case 'contact': {
				contactFilter = {
					criteria: [
						{
							property: ContactFilterCriteriaProperty.NamePrefix,
							value: searchValue,
						},
					],
				};
				break;
			}
			case 'company': {
				companyFilter = {
					criteria: [
						{
							property: CompanyFilterCriteriaProperty.NamePrefix,
							value: searchValue,
						},
					],
				};
				break;
			}
			case 'phone': {
				contactFilter = {
					criteria: [
						{
							property: ContactFilterCriteriaProperty.PhoneNumber,
							value: searchValue,
						},
					],
				};
				companyFilter = {
					criteria: [
						{
							property: CompanyFilterCriteriaProperty.PhoneNumber,
							value: searchValue,
						},
					],
				};
				break;
			}
			case 'email': {
				contactFilter = {
					criteria: [
						{
							property: ContactFilterCriteriaProperty.Email,
							value: searchValue,
						},
					],
				};
				break;
			}
			default: {
				break;
			}
		}
	}
	const leadSearchQuery = useLeadSearchQuery({
		enabled: searchType === 'all' && Boolean(searchValue),
		search: searchValue,
		refetchOnWindowFocus: false,
	});
	const leadFilterByContactQuery = useLeadFilterByContactQuery({
		enabled: Boolean(contactFilter),
		filter: contactFilter,
		meta: {
			toastErrorMessage: 'Error fetching contact leads',
			errorLogging: { category: 'SearchModal', action: 'LeadFilterByContactQuery' },
		},
		swallowHttpErrors: true,
		refetchOnWindowFocus: false,
	});
	const leadFilterByCompanyQuery = useLeadFilterByCompanyQuery({
		enabled: Boolean(companyFilter),
		filter: companyFilter,
		meta: {
			toastErrorMessage: 'Error fetching company leads',
			errorLogging: { category: 'SearchModal', action: 'LeadFilterByCompanyQuery' },
		},
		swallowHttpErrors: true,
		refetchOnWindowFocus: false,
	});
	const activeQueries: UseQueryResult<ILeadSearchView[], Error>[] = [];
	if (searchType === 'all' && Boolean(searchValue)) {
		activeQueries.push(leadSearchQuery);
	}
	if (contactFilter) {
		activeQueries.push(leadFilterByContactQuery);
	}
	if (companyFilter) {
		activeQueries.push(leadFilterByCompanyQuery);
	}

	const searchKey = payload?.key;

	const isCallFromUnknown = searchKey === SearchKeyOptions.CallFromUnknown;

	useEffect(() => {
		setSearchValue(initialQuery);
	}, [initialQuery]);

	useEffect(() => {
		if (!isOpen) {
			setSearchValue('');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen]);

	const onSearch: React.FormEventHandler<HTMLFormElement> = e => {
		e.preventDefault();
		const formData = new FormData(e.target as HTMLFormElement);
		const value = formData.get('search') as string;
		const type = formData.get('searchType') as 'contact' | 'company' | 'phone' | 'email';
		setSearchType(type);
		setSearchValue(value);
	};

	const renderResults = () => {
		if (!activeQueries.length) {
			return null;
		}
		if (activeQueries.some(query => query.isLoading)) {
			return <LoadingSpinner className={css(aidaBaseStyleSheet.absoluteCenter)} type='large' />;
		}
		if (!activeQueries.every(query => query.isSuccess)) {
			return null;
		}

		if (activeQueries.every(query => !query.data?.length)) {
			return <div className={css(styleSheet.noResults)}>{`No results for "${searchValue}"`}</div>;
		}

		return (
			<FlipMove
				enterAnimation={false}
				leaveAnimation={false}
				staggerDelayBy={50}
				staggerDurationBy={10}
				appearAnimation={true}
			>
				{activeQueries
					.flatMap(query => query.data)
					.map(lead => (
						<div
							className={css(styleSheet.leadCard)}
							key={`global-lead-search-${lead.company.id}-${lead.contactReferences?.[0]?.id}`}
						>
							<LeadCard source={LeadServedSource.Search} lead={lead} />
						</div>
					))}
			</FlipMove>
		);
	};

	return (
		<Dialog
			open={isOpen}
			onOpenChange={open => {
				if (!open) {
					onRequestClose(null, true);
				}
			}}
			dismissOnOutsideClick={false}
		>
			<DialogBackdrop styles={[styleSheet.backdrop]} />
			<DialogPopup styles={[styleSheet.dialogPopup]} dialogCloseStyles={[styleSheet.close]} overlay={false}>
				<div className={css(styleSheet.header)}>
					{isCallFromUnknown ? (
						<div className={css(styleSheet.title, styleSheet.smallTitle)}>
							We do not recognize the incoming call number, but you can search for an existing lead
						</div>
					) : (
						<div className={css(styleSheet.title)}>Search</div>
					)}
				</div>
				<form className={css(styleSheet.searchRow)} onSubmit={onSearch}>
					<div className={css(styleSheet.searchGroup)}>
						<Select
							items={searchTypeOptions}
							defaultValue='all'
							name='searchType'
							onOpenChange={open => {
								if (!open && searchInputRef.current) {
									// focus the input when the select is closed
									searchInputRef.current.focus();
								}
							}}
						>
							<SelectTrigger styles={[styleSheet.searchSelect]}>
								<SelectValue />
							</SelectTrigger>
							<SelectContent
							/* onCloseAutoFocus={e => {
									// prevents default behavior of transferring focus to select trigger so we can keep focus on the input
									e.preventDefault();
								}} */
							>
								{searchTypeOptions.map(option => (
									<SelectItem key={option.value} value={option.value}>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<InputWrapper styles={[styleSheet.searchInput]}>
							<SearchIcon2 width={20} height={20} />
							<Input ref={searchInputRef} autoFocus placeholder='Search leads...' name='search' />
						</InputWrapper>
					</div>
					<Button styles={[bs.hAuto]} variant='secondary' type='submit'>
						Search
					</Button>
				</form>
				<div className={css(styleSheet.resultsContainer)}>
					{isCallFromUnknown && !searchValue ? (
						<div className={css(styleSheet.placeholder)}>
							<img className={css(styleSheet.placeholderImage)} src={PlaceholderImageSource} />
						</div>
					) : null}
					{renderResults()}
				</div>
			</DialogPopup>
		</Dialog>
	);
};

export const SearchModal = observer(SearchModalBase);
